/**
 * StaleConsentModal — blocking modal for authed users whose accepted
 * ToS or Privacy version is older than the current version (J-X13 §
 * Steps existing user).
 *
 * Mount strategy: mounted at App root (`main.tsx`). On every route change
 * it calls `GET /api/me/consent-status`. When the response indicates a
 * stale version, the modal renders over the page and blocks until the
 * user clicks "I agree" (calls `POST /api/me/accept-consent`) or
 * "Sign out". Anonymous responses (401/no-cookie) are silently ignored —
 * the modal only applies to authed users.
 *
 * Resilience: a failed status fetch or accept call falls back to the
 * silent path (the user is not blocked by transient backend errors). The
 * stored versions are eventually-consistent — the server-side middleware
 * is the source of truth.
 *
 * Inv 12 (no console.log), Inv 13 (no any), Inv 14 (no internal nav),
 * Inv 15 (Button atom from barrel).
 */

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui';
import { TOS_VERSION, PRIVACY_VERSION } from '@bimassess/db/legal-versions';
import { apiFetch } from '@/lib/api-client';

interface ConsentStatusResponse {
  ok: true;
  data: {
    /** True if the user is signed in. False/missing for anonymous. */
    authenticated: boolean;
    /** Stored versions on the user row — null for legacy / pre-consent rows. */
    tos_version_accepted: string | null;
    privacy_version_accepted: string | null;
    /** Current versions the server is enforcing (echoes constants). */
    current_tos_version: string;
    current_privacy_version: string;
  };
}

type StatusEnvelope =
  | ConsentStatusResponse
  | { ok: false; error: { code: string; message: string } };

async function fetchConsentStatus(): Promise<ConsentStatusResponse['data'] | null> {
  try {
    const res = await apiFetch('/api/me/consent-status', { credentials: 'same-origin' });
    if (!res.ok && res.status !== 200) return null;
    const body = (await res.json()) as StatusEnvelope;
    if (!body.ok) return null;
    return body.data;
  } catch {
    return null;
  }
}

async function postAcceptConsent(): Promise<boolean> {
  try {
    const res = await apiFetch('/api/me/accept-consent', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        tos_version: TOS_VERSION,
        privacy_version: PRIVACY_VERSION,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function isStale(data: ConsentStatusResponse['data']): boolean {
  if (!data.authenticated) return false;
  // Legacy rows (sandbox-migrated, no version stored) are treated as stale.
  if (data.tos_version_accepted !== data.current_tos_version) return true;
  if (data.privacy_version_accepted !== data.current_privacy_version) return true;
  return false;
}

export function StaleConsentModal(): ReactElement | null {
  const location = useLocation();
  const [stale, setStale] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    const data = await fetchConsentStatus();
    if (data === null) {
      setStale(false);
      return;
    }
    setStale(isStale(data));
  }, []);

  useEffect(() => {
    // Skip the check on the legal pages themselves and on auth pages — those
    // surfaces must be reachable when consent is stale (the modal links to
    // them, and the sign-out button needs sign-in to work after).
    const skipPaths = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password'];
    if (location.pathname.startsWith('/legal/')) return;
    if (skipPaths.includes(location.pathname)) return;
    void refresh();
  }, [location.pathname, refresh]);

  if (!stale) return null;

  async function onAgree(): Promise<void> {
    if (!acknowledged) return;
    setSubmitting(true);
    const ok = await postAcceptConsent();
    setSubmitting(false);
    if (ok) {
      setStale(false);
      setAcknowledged(false);
    }
  }

  async function onSignOut(): Promise<void> {
    try {
      await apiFetch('/api/auth/sign-out', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } catch {
      // ignore — we still redirect
    }
    window.location.href = '/sign-in';
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="stale-consent-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 px-4"
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card p-6 shadow-card">
        <h2
          id="stale-consent-title"
          className="font-sans text-h2 font-medium tracking-tight"
        >
          Updated Terms and Privacy Policy
        </h2>
        <p className="mt-4 font-sans text-body text-ink">
          We have updated our{' '}
          <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline">
            Privacy Policy
          </a>
          . Please review and accept the updated documents to continue using
          BIMAssess.
        </p>
        <label className="mt-6 flex items-start gap-3">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1.5"
          />
          <span className="font-sans text-body text-ink">
            I have read and agree to the updated Terms of Service and Privacy
            Policy.
          </span>
        </label>
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button variant="secondary" size="sm" type="button" onClick={onSignOut}>
            Sign out
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={onAgree}
            disabled={!acknowledged || submitting}
          >
            {submitting ? 'Saving…' : 'I agree'}
          </Button>
        </div>
      </div>
    </div>
  );
}
